import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import { CareItem } from 'src/replicant/buruburu/care/care.ruleset';
import StateObserver from 'src/StateObserver';
import { ImageScaleView, ImageSliceView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { Care } from 'src/replicant/buruburu/care';
import { ImageScaleViewOpts } from '@play-co/timestep-core/lib/ui/ImageScaleView';
import { parseAmount } from 'src/lib/utils';
import sounds from 'src/lib/sounds';
import { iapClient } from 'src/lib/iap/iapClient';
import { runShopOutOfCoinsSequence } from '../shop/shop.sequence';

type CloseHandler = (result?: boolean) => void;
const ui = uiConfig.buruburu.care.confirmation;

export default class PopupConfirmCareItem extends PopupBasic {
  private close: CloseHandler;
  private cost: LangBitmapFontTextView;
  private balance: LangBitmapFontTextView;
  private okButton: ButtonScaleViewWithText;
  private careItemId = '';
  private pointsBalance = 0;

  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => opts.close(),
      ...ui.popup,
      tracking: { feature: 'care', $subFeature: 'popup-confirmcareitem' },
    });

    this.close = opts.close;
    this.buttonClose.removeFromSuperview();

    this.message.updateOpts({
      ...ui.message,
      width: this.box.style.width - 2 * ui.message.x,
    });

    this.box.addSubview(
      new ImageScaleView({
        ...ui.underline,
        width: this.message.style.width,
      }),
    );

    const costRow = this.box.addSubview(new ImageSliceView(ui.costRow));

    costRow.addSubview(
      new LangBitmapFontTextView({
        ...ui.highlightRowLabel,
        localeText: () => i18n('careItem.pointsRequired'),
      }),
    );

    this.cost = new LangBitmapFontTextView({
      ...ui.highlightRowValue,
      superview: costRow,
    });

    costRow.addSubview(
      new ImageScaleView({
        ...ui.currency,
        x: costRow.style.width - ui.currency.width,
      }),
    );

    const balanceRow = this.box.addSubview(new ImageSliceView(ui.balanceRow));

    balanceRow.addSubview(
      new LangBitmapFontTextView({
        ...ui.highlightRowLabel,
        localeText: () => i18n('careItem.pointsRemaining'),
      }),
    );

    this.balance = new LangBitmapFontTextView({
      ...ui.highlightRowValue,
      superview: balanceRow,
    });

    balanceRow.addSubview(
      new ImageScaleView({
        ...ui.currency,
        x: balanceRow.style.width - ui.currency.width,
      }),
    );

    this.box.addSubview(
      new ButtonScaleViewWithText({
        ...ui.backButton,
        localeText: () => i18n('basic.back'),
        onClick: async () => {
          sounds.sfx.play('cancel');
          return opts.close(false);
        },
        tracking: {
          feature: 'care',
          $subFeature: 'popup-confirmcareitem-back',
        },
      }),
    );

    this.okButton = new ButtonScaleViewWithText({
      ...ui.okButton,
      superview: this.box,
      localeText: () => i18n('basic.okay'),
      onClick: async () => {
        sounds.sfx.play('ok');

        if (this.pointsBalance < 0 && iapClient.paymentsAvailable) {
          const careItem = Care.ruleset.careItems[this.careItemId];
          await runShopOutOfCoinsSequence(careItem.price);
          this.refresh();
        } else {
          return opts.close(true);
        }
      },
      tracking: { feature: 'care', $subFeature: 'popup-confirmcareitem-ok' },
    });
  }

  init({ careItemId }: { careItemId: CareItem['id'] }) {
    const careItem = Care.ruleset.careItems[careItemId];
    super.init({
      title: i18n('careItem.confirmTitle'),
      message: i18n('careItem.confirmMessage'),
    });
    this.careItemId = careItemId;
    this.box.addSubview(new CareItemPanel({ careItem }));
    this.cost.localeText = () => parseAmount(careItem.price);
    this.refresh();
  }

  refresh() {
    const careItem = Care.ruleset.careItems[this.careItemId];
    const points = StateObserver.getState().user.inventory.points;
    const balance = points - careItem.price;
    this.balance.localeText = () => parseAmount(balance);
    this.pointsBalance = balance;

    if (!iapClient.paymentsAvailable) {
      if (balance < 0) {
        this.balance.updateOpts({ color: 'red' });
        this.okButton.setDisabled(true);
      }
    } else {
      if (balance < 0) {
        this.balance.updateOpts({ color: 'red' });
        this.okButton.localeText = () => i18n('careItem.buyPoints');
      } else {
        this.balance.updateOpts({ color: '#9A7171' });
        this.okButton.localeText = () => i18n('basic.okay');
      }
    }
  }
}

type CareItemPanelOpts = Omit<
  ImageScaleViewOpts,
  'image' | 'width' | 'height'
> & {
  careItem: CareItem;
};

class CareItemPanel extends ImageScaleView {
  constructor({ careItem, ...opts }: CareItemPanelOpts) {
    super({
      ...opts,
      ...ui.careItemPanel,
    });

    // icon
    this.addSubview(
      new ImageScaleView({
        scaleMethod: 'contain',
        x: 20,
        y: 20,
        width: 156,
        height: 137,
        image: ui.careItemIcons[careItem.id],
      }),
    );

    // name
    const name = careItem[`name_${getLocaleSuffix()}`];
    this.addSubview(
      new LangBitmapFontTextView({
        font: 'Body',
        color: '#9A7171',
        y: 25,
        size: 30,
        x: 190,
        height: 39,
        width: 283,
        localeText: () => name.replace(/\n/g, ''), // Strip newlines
        verticalAlign: 'bottom',
      }),
    );

    // description
    this.addSubview(
      new LangBitmapFontTextView({
        size: 25,
        x: 190,
        y: 85,
        width: 283,
        height: 64,
        font: 'Body',
        color: '#9A7171',
        localeText: () => careItem[`desc_${getLocaleSuffix()}`],
        wordWrap: StateObserver.getState().ui.locale === 'en',
      }),
    );
  }
}

const getLocaleSuffix = () =>
  StateObserver.getState().ui.locale === 'en' ? 'en' : 'jp';
